import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { api, idr, fmtNum } from "../lib/api";
import EmployeeAvatar from "../components/hris/EmployeeAvatar";
import { Trophy, Crown, Medal, Flame, RefreshCw, Maximize2, Minimize2, Store, TrendingUp, Volume2, VolumeX, Users, Sparkles, ArrowUpRight } from "lucide-react";
import { motion, MotionConfig, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { MonthPicker } from "@/components/ui/date-picker";
import { bulanIniISO } from "@/lib/tanggal";

import ThemeControl from "@/components/ui/ThemeControl";
import "./Leaderboard.css";

const REFRESH_MS = 45000;

// Play a short triumphant fanfare using Web Audio API — no external files needed
function playFanfare() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0.22;
    master.connect(ctx.destination);

    // Ascending arpeggio: C5, E5, G5, C6 — brass-ish (sawtooth + lowpass)
    const notes = [523.25, 659.25, 783.99, 1046.5];
    const start = ctx.currentTime + 0.02;
    const stepDur = 0.14;
    const lastRing = 0.7;

    notes.forEach((freq, i) => {
      const t0 = start + i * stepDur;
      const t1 = t0 + (i === notes.length - 1 ? lastRing : stepDur * 1.1);
      // Two detuned oscs for richness
      [-6, +6].forEach(cents => {
        const osc = ctx.createOscillator();
        osc.type = "sawtooth";
        osc.frequency.value = freq;
        osc.detune.value = cents;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, t0);
        g.gain.linearRampToValueAtTime(0.9, t0 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, t1);
        // simple lowpass to soften harshness
        const lp = ctx.createBiquadFilter();
        lp.type = "lowpass"; lp.frequency.value = 2600;
        osc.connect(g); g.connect(lp); lp.connect(master);
        osc.start(t0); osc.stop(t1 + 0.05);
      });
    });

    // Percussive tail hit on the last note
    const hitT = start + (notes.length - 1) * stepDur + 0.02;
    const hit = ctx.createOscillator();
    hit.type = "triangle"; hit.frequency.value = 130;
    const hg = ctx.createGain();
    hg.gain.setValueAtTime(0.6, hitT);
    hg.gain.exponentialRampToValueAtTime(0.001, hitT + 0.5);
    hit.connect(hg); hg.connect(master);
    hit.start(hitT); hit.stop(hitT + 0.55);

    setTimeout(() => ctx.close(), 2000);
  } catch (e) {
    /* browser autoplay policy may block — silent fail */
  }
}

export default function Leaderboard() {
  const [periode, setPeriode] = useState(bulanIniISO());
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [showBranchFilter, setShowBranchFilter] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const requestVersion = useRef(0);
  const podiumRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const [fullscreen, setFullscreen] = useState(false);
  const [tick, setTick] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    const v = localStorage.getItem("leaderboard_sound");
    return v === null ? true : v === "1";
  });
  const prevChampionRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("leaderboard_sound", soundOn ? "1" : "0");
  }, [soundOn]);

  const fireConfetti = useCallback(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    confetti({ particleCount: 100, spread: 95, origin: { x: 0.5, y: 0.4 }, colors: ["#f4bd53", "#80b3ff", "#52c9b2"], disableForReducedMotion: true });
  }, []);

  const celebrate = useCallback((name) => {
    fireConfetti();
    if (soundOn) playFanfare();
    toast.success(`🎉 Juara baru: ${name}!`, { duration: 5000 });
  }, [fireConfetti, soundOn]);

  const load = useCallback(() => {
    const version = ++requestVersion.current;
    setLoading(true); setLoadError("");
    const p = new URLSearchParams({ periode, limit: "10" });
    if (selectedBranches.length > 0) p.set("branches", selectedBranches.join(","));
    api.get(`/sales/leaderboard?${p}`)
      .then(r => {
        if (version !== requestVersion.current) return;
        const board = r.data?.leaderboard || [];
        const newChamp = board[0]?.nama || null;
        const prev = prevChampionRef.current;
        if (prev?.scope === p.toString() && newChamp && prev.name && prev.name !== newChamp) celebrate(newChamp);
        prevChampionRef.current = { scope: p.toString(), name: newChamp };
        setData(r.data);
        setCountdown(REFRESH_MS / 1000);
      })
      .catch(err => { if (version === requestVersion.current) setLoadError(err?.response?.data?.detail || "Podium belum dapat dimuat. Coba lagi."); })
      .finally(() => { if (version === requestVersion.current) setLoading(false); });
  }, [periode, selectedBranches, celebrate]);

  useEffect(() => { load(); const pending = requestVersion; return () => { pending.current++; }; }, [load, tick]);
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === document.documentElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), REFRESH_MS);
    const cd = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => { clearInterval(iv); clearInterval(cd); };
  }, []);

  const toggleFs = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen?.();
      else if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
      else toast.info("Browser ini belum mendukung layar penuh.");
    } catch { toast.error("Layar penuh belum dapat dibuka di browser ini."); }
  };

  const top3 = useMemo(() => (data?.leaderboard || []).slice(0, 3), [data]);
  const rest = useMemo(() => (data?.leaderboard || []).slice(3), [data]);
  const branchesUsed = useMemo(() => data?.branches_used || [], [data]);
  const s = data?.summary || {};
  // Brief #25: bawaan podium menampilkan POIN SAJA — omzet/transaksi/akuisisi_rp
  // baru muncul kalau owner menyalakan `tampilkan_omzet_podium` di Konfigurasi Poin.
  const tampilkanOmzet = !!data?.tampilkan_omzet;
  // Kalimat penyemangat custom (Konfigurasi Poin) — kalau lebih dari satu,
  // bergantian setiap siklus auto-refresh (memakai `tick` yang sudah ada).
  const kataMotivasi = data?.kata_motivasi || [];
  const motivasiAktif = kataMotivasi.length > 0 ? kataMotivasi[tick % kataMotivasi.length] : null;

  // Reorder podium visually: 2 · 1 · 3
  const podiumOrder = top3; // CSS orders 2 / 1 / 3 on desktop; mobile reads rank 1 first.

  const MAX_BRANCH_SELECT = 4;
  const toggleBranch = (b) => setSelectedBranches(prev => {
    if (prev.includes(b)) return prev.filter(x => x !== b);
    if (prev.length >= MAX_BRANCH_SELECT) {
      toast.warning(`Maksimal ${MAX_BRANCH_SELECT} cabang bisa dipilih bersamaan`);
      return prev;
    }
    return [...prev, b];
  });
  const selectAllBranches = () => setSelectedBranches([]);

  return <MotionConfig reducedMotion="user">
    <div className="eh-leaderboard" ref={podiumRef} data-testid="leaderboard-page">
      <div className="lb-wrap">
        <header className="lb-heading">
          <div className="lb-title"><span className="lb-title-icon"><Trophy size={27} strokeWidth={1.7}/></span><div><p>PENCAPAIAN TIM</p><h1>Podium Sales</h1><span>Setiap usaha membawa kita lebih jauh.</span></div></div>
          <div className="lb-tools"><ThemeControl compact/>
            <button onClick={() => { const next = !soundOn; setSoundOn(next); if (next) playFanfare(); }} data-testid="leaderboard-sound" aria-pressed={soundOn} aria-label={soundOn ? "Matikan suara podium" : "Aktifkan suara podium"} title={soundOn ? "Matikan suara" : "Aktifkan suara"}>{soundOn ? <Volume2 size={18}/> : <VolumeX size={18}/>}</button>
            <button onClick={toggleFs} data-testid="leaderboard-fs" title={fullscreen ? "Keluar layar penuh" : "Layar penuh"} aria-label={fullscreen ? "Keluar layar penuh" : "Layar penuh"}>{fullscreen ? <Minimize2 size={18}/> : <Maximize2 size={18}/>}</button>
          </div>
        </header>
        <div className="lb-filterbar"><div className="lb-filters"><label><span className="sr-only">Periode podium</span><MonthPicker value={periode} onChange={setPeriode} data-testid="leaderboard-periode"/></label>
          <button onClick={() => setShowBranchFilter(v => !v)} data-testid="leaderboard-branch-toggle" aria-expanded={showBranchFilter} aria-controls="lb-branches"><Store size={16}/>{selectedBranches.length ? `${selectedBranches.length} cabang` : "Semua cabang"}</button>
        </div><div className="lb-live"><span><i/> Diperbarui otomatis</span><button onClick={load} disabled={loading} data-testid="leaderboard-refresh" aria-label="Perbarui podium" title="Perbarui podium"><RefreshCw size={16} className={loading ? "lb-spinning" : ""}/><span>{loading ? "Memuat…" : `${countdown} dtk`}</span></button></div></div>
        {showBranchFilter && <div className="lb-branch-panel" id="lb-branches" data-testid="leaderboard-branch-panel"><div><strong>Pilih cabang</strong><button onClick={selectAllBranches} data-testid="leaderboard-branch-all">Semua cabang</button></div><div className="lb-branch-options">
          {!branchesUsed.length && <p>Belum ada cabang pada data karyawan aktif.</p>}
          {branchesUsed.map(b => <label key={b} data-testid={`leaderboard-branch-cb-${b}`}><input type="checkbox" checked={selectedBranches.includes(b)} onChange={() => toggleBranch(b)}/>{b}</label>)}
        </div><p>Pilih hingga {MAX_BRANCH_SELECT} cabang. Tanpa centang berarti seluruh cabang.</p></div>}
        {loadError && <div className="lb-error" role="alert">{loadError} <button onClick={load}>Coba lagi</button>{data && <span>Data terakhir masih ditampilkan.</span>}</div>}
        <div className={`lb-summary ${tampilkanOmzet ? "with-revenue" : ""}`}>
          <SumCard label="Sales aktif" value={data ? fmtNum(s.n_penjual_aktif || 0) : "—"} icon={Users} sub="Berkontribusi periode ini"/>
          <SumCard label="Poin terkumpul" value={data ? fmtNum(s.total_poin || 0) : "—"} icon={Flame} sub="Hasil kerja seluruh tim"/>
          {tampilkanOmzet && <SumCard label="Total omzet" value={idr(s.total_omzet || 0)} icon={TrendingUp} sub="Pada periode terpilih"/>}
        </div>
        <section className="lb-podium-section" aria-label="Tiga sales teratas"><div className="lb-section-heading"><div><p>APRESIASI UNTUK TIM</p><h2>Bintang periode ini <Sparkles size={19}/></h2></div><span>Top {Math.min(top3.length, 3)}</span></div>
          {!data && loading ? <div className="lb-empty" role="status"><RefreshCw className="lb-spinning"/><p>Menyiapkan podium…</p></div> : !podiumOrder.length && !loadError ? <EmptyPodium/> :
            <div className={`lb-podium lb-count-${podiumOrder.length}`}>{podiumOrder.map(p => <PodiumCard key={p.employee_id || p.nama} p={p} tampilkanOmzet={tampilkanOmzet} reduceMotion={reduceMotion}/>)}</div>}
        </section>
        {rest.length > 0 && <section className="lb-ranking"><div className="lb-section-heading"><div><p>TERUS MELANGKAH</p><h2>Peringkat berikutnya</h2></div><ArrowUpRight size={20}/></div><div className="lb-rank-list">
          {rest.map((r, i) => <motion.article key={r.employee_id || r.nama} initial={reduceMotion ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, duration: 0.25 }} data-testid={`rank-${r.rank}`} className="lb-rank-row">
            <span className="lb-rank-number">{r.rank}</span><EmployeeAvatar employeeId={r.employee_id} nama={r.nama} size={42}/><div className="lb-rank-person"><h3>{r.nama}</h3><p>{r.branch}{tampilkanOmzet ? ` · ${r.transaksi_sales} transaksi` : ""}</p></div>
            <div className="lb-rank-result"><strong>{fmtNum(r.total_poin)} <small>poin</small></strong><p data-testid={`rank-upselling-${r.rank}`}>Upselling {fmtNum(r.upselling_pct ?? 0)}% · Skor {r.upselling_achievement_pct == null ? "—" : `${fmtNum(r.upselling_achievement_pct)}%`}</p>{tampilkanOmzet && <p>{idr(r.omzet)}</p>}</div>
          </motion.article>)}
        </div></section>}
        {motivasiAktif && <footer className="lb-motivation" data-testid="leaderboard-motivasi"><Sparkles size={18}/><p>“{motivasiAktif}”</p></footer>}
      </div>
    </div>
  </MotionConfig>;
}

function PodiumCard({ p, tampilkanOmzet, reduceMotion }) {
  const first = p.rank === 1;
  return <motion.article initial={reduceMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: (p.rank - 1) * 0.08 }} className={`lb-champion lb-place-${p.rank}`} data-testid={`podium-${p.rank}`}>
    <div className="lb-card-top"><span className="lb-place"><Medal size={15}/> Peringkat {p.rank}</span>{first && <Crown size={24} className="lb-crown" aria-label="Juara periode ini"/>}</div>
    <div className="lb-person"><div className="lb-portrait"><EmployeeAvatar employeeId={p.employee_id} nama={p.nama} size={88}/><span>{p.rank}</span></div><h3>{p.nama}</h3><p>{p.branch || "Tim sales"}</p></div>
    <div className="lb-points"><strong>{fmtNum(p.total_poin)}</strong><span>poin terkumpul</span></div>
    <dl className="lb-detail"><div><dt>Upselling</dt><dd data-testid={`podium-upselling-${p.rank}`}>{fmtNum(p.upselling_pct ?? 0)}%</dd></div><div><dt>Skor upselling</dt><dd data-testid={`podium-upselling-skor-${p.rank}`}>{p.upselling_achievement_pct == null ? "—" : `${fmtNum(p.upselling_achievement_pct)}%`}</dd></div>
      {tampilkanOmzet && <><div><dt>Omzet</dt><dd>{idr(p.omzet)}</dd></div><div><dt>Transaksi sales + akuisisi</dt><dd>{p.transaksi_sales} + {p.transaksi_acq}</dd></div></>}
    </dl>
    <div className="lb-card-foot">{first ? "Langkah hebat, terus bertumbuh!" : "Setiap langkah berarti."}</div>
  </motion.article>;
}
function SumCard({ label, value, icon: Icon, sub }) {
  return <div className="lb-stat"><span className="lb-stat-icon"><Icon size={21}/></span><div><p>{label}</p><strong>{value}</strong><small>{sub}</small></div></div>;
}
function EmptyPodium() {
  return <div className="lb-empty"><Medal size={38}/><h3>Podium menunggu bintang berikutnya</h3><p>Belum ada penjualan pada periode ini. Pencapaian tim akan tampil di sini.</p></div>;
}
